// CAMBIAR EL ESTATUS DE VARIAS POLIZAS A LA VEZ.
//
// Luis Rodriguez, 2026-09-10, por soporte: pregunto como cambiar el estatus de
// varias polizas de golpe y le contestaron que no se podia. Era verdad — habia
// borrado masivo, pero para poner veinte polizas en un estatus habia que abrir
// las veinte fichas, una por una.
//
// LAS TRES COSAS QUE ESTA PANTALLA TIENE QUE DECIR ANTES DE QUE NADIE PULSE, y
// que son su razon de ser (regla de oro 20: lo que la pantalla no dice se
// descubre tarde, y casi siempre en una llamada con un cliente delante):
//
//   1. CUANTAS cambian de verdad. Si de veinte elegidas ocho ya estaban en ese
//      estatus, el boton no puede decir veinte.
//   2. QUE CANCELAR PIDE FECHA. La agencia cuadra contra esa fecha las
//      devoluciones de comision (Lissette 2026-07-22), asi que el servidor
//      rechaza el lote entero sin ella. Mejor pedirla aqui que enterarse por un
//      error rojo despues de elegir veinte.
//   3. QUE LAS AUTOMATIZACIONES NO SE DISPARAN salvo que se marque. Cancelar
//      una poliza puede disparar la automatizacion de retencion, que manda
//      correos y SMS A CLIENTES DE VERDAD: veinte de golpe, sin avisar, no se
//      pueden recoger.

import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Modal, Select, Input } from './ui.jsx';
import { getAllPolicyStatuses } from '../policyStatuses.js';
import { bulkUpdatePolicyStatus } from '../api.js';
import { toast } from './Toast.jsx';
// Las cuentas viven sueltas, sin React, para que la prueba del servidor pueda
// comprobar que lo que se promete aqui es lo que alli se hace.
import { resumenDelCambio, pideFechaDeCancelacion, sePuedeCambiar } from '../estatusEnLote.js';

export function CambiarEstatusModal({ policies = [], lang = 'es', apiMode = true, onClose, onDone }) {
  const es = lang === 'es';
  const [destino, setDestino] = useState('');
  const [fecha, setFecha] = useState('');
  const [automatizaciones, setAutomatizaciones] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  // OJO con el numero del boton: es el de las que CAMBIAN, no el de las
  // elegidas. Decia "Cambiar 42" sobre una seleccion donde 41 cambiaban y una
  // ya estaba cancelada — el mismo numero que luego no cuadra con el resultado
  // (regla 142). Se vio en el navegador, no en el build.

  const estatuses = useMemo(() => getAllPolicyStatuses(), []);
  const resumen = useMemo(() => resumenDelCambio(policies, destino, fecha), [policies, destino, fecha]);
  const pideFecha = pideFechaDeCancelacion(destino);
  const puede = sePuedeCambiar({ destino, fecha, cambian: resumen.cambian }) && !ocupado;

  const aplicar = async () => {
    if (!puede) return;
    setOcupado(true);
    const ids = policies.map((p) => p.id);
    try {
      let cambiadas = 0; let sinCambio = 0; let fuera = 0;
      if (apiMode) {
        // El servidor corta en 500 por peticion; se manda de 200 en 200 igual
        // que el borrado masivo, para que una limpieza grande pase entera.
        for (let i = 0; i < ids.length; i += 200) {
          const r = await bulkUpdatePolicyStatus(ids.slice(i, i + 200), destino, {
            ...(pideFecha ? { cancellationDate: fecha } : {}),
            ejecutarAutomatizaciones: pideFecha && automatizaciones,
          });
          cambiadas += r.cambiadas; sinCambio += r.sinCambio; fuera += r.fuera;
        }
      } else {
        cambiadas = resumen.cambian; sinCambio = resumen.yaEstan;
      }
      // El resumen sale de lo que CONTESTO el servidor, no de lo que se penso
      // mandar: decir "20 cambiadas" cuando cambiaron 12 es la regla 142.
      const partes = [es ? `${cambiadas} póliza(s) cambiada(s)` : `${cambiadas} policy(ies) changed`];
      if (sinCambio) partes.push(es ? `${sinCambio} ya estaba(n) así` : `${sinCambio} already had it`);
      if (fuera) partes.push(es ? `${fuera} fuera de tu alcance` : `${fuera} outside your scope`);
      toast.success(partes.join(' · '));
      if (typeof onDone === 'function') onDone({ ids, destino, fecha: pideFecha ? fecha : '', cambiadas });
      onClose();
    } catch (e) {
      // El motivo tal cual: "no se pudo" a secas obliga a escribir a soporte
      // para saber si fue la fecha, el permiso o el alcance.
      toast.error(e.message || (es ? 'No se pudo cambiar el estatus' : 'Could not change the status'));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Modal title={es ? 'Cambiar estatus de las seleccionadas' : 'Change status of selected'} onClose={() => !ocupado && onClose()}>
      <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', marginBottom: '.9rem' }}>
        {es ? `${resumen.total} póliza(s) seleccionada(s)` : `${resumen.total} policy(ies) selected`}
      </div>

      <Select label={es ? 'Nuevo estatus' : 'New status'} value={destino} onChange={(e) => setDestino(e.target.value)}>
        <option value="">{es ? '— elegir estatus —' : '— pick a status —'}</option>
        {estatuses.map((s) => (
          <option key={s.id} value={s.id}>{es ? (s.labelEs || s.label) : (s.label || s.labelEs)}</option>
        ))}
      </Select>

      {/* CUANTAS cambian de verdad. Solo cuando ya hay un estatus elegido: sin
          eso la frase no dice nada. */}
      {!!destino && (
        <div style={{
          marginTop: '.7rem', padding: '.6rem .7rem', borderRadius: 8, fontSize: '.72rem', lineHeight: 1.55,
          background: 'rgba(0,212,255,.07)', border: '1px solid rgba(0,212,255,.25)', color: 'var(--c-text2,#7aa3c0)',
        }}>
          {es
            ? `Cambian ${resumen.cambian}${resumen.yaEstan ? ` · ${resumen.yaEstan} ya está(n) así y no se tocan` : ''}.`
            : `${resumen.cambian} will change${resumen.yaEstan ? ` · ${resumen.yaEstan} already like that, won't be touched` : ''}.`}
        </div>
      )}

      {pideFecha && (
        <div style={{ marginTop: '.8rem' }}>
          <Input
            label={es ? 'Fecha de cancelación (obligatoria)' : 'Cancellation date (required)'}
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)} />
          <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginTop: 4, lineHeight: 1.5 }}>
            {es
              ? 'Se les pone la misma fecha a todas. Es con la que se cuadran las devoluciones de comisión, por eso no puede quedarse vacía.'
              : 'The same date is written on all of them. Commission clawbacks are reconciled against it, so it cannot be left empty.'}
          </div>
        </div>
      )}

      {/* LAS AUTOMATIZACIONES. Solo si de verdad hay polizas que PASAN a
          cancelada: un aviso que sale siempre deja de leerse. */}
      {pideFecha && resumen.saltanACancelada > 0 && (
        <label style={{
          display: 'flex', gap: '.55rem', alignItems: 'flex-start', marginTop: '.8rem', cursor: 'pointer',
          padding: '.6rem .7rem', borderRadius: 8,
          background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.35)',
        }}>
          <input type="checkbox" checked={automatizaciones} onChange={(e) => setAutomatizaciones(e.target.checked)}
            style={{ marginTop: 2, cursor: 'pointer' }} />
          <span style={{ fontSize: '.72rem', lineHeight: 1.55, color: 'var(--c-amber,#f59e0b)' }}>
            {es
              ? `Ejecutar las automatizaciones de cancelación (le escribirían a ${resumen.saltanACancelada} cliente(s) por correo o SMS). Sin marcar, el estatus cambia y no se envía nada.`
              : `Run the cancellation automations (they would email or text ${resumen.saltanACancelada} client(s)). Left unticked, the status changes and nothing is sent.`}
          </span>
        </label>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem', marginTop: '1.1rem' }}>
        <button type="button" className="dkt-btn-g" onClick={onClose} disabled={ocupado}>
          {es ? 'Cancelar' : 'Cancel'}
        </button>
        <button type="button" className="dkt-btn-p" onClick={aplicar} disabled={!puede}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, opacity: puede ? 1 : 0.5 }}>
          <RefreshCw size={14} />
          {ocupado
            ? (es ? 'Cambiando…' : 'Changing…')
            : (es ? `Cambiar ${resumen.cambian}` : `Change ${resumen.cambian}`)}
        </button>
      </div>
    </Modal>
  );
}
